import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './auth/AuthProvider';
import RequireAuth from './auth/RequireAuth';
import Login from './app/Login';
import Register from './app/Register';
import JoinCircle from './app/JoinCircle';
import JoinReferral from './app/JoinReferral';
import DashboardLayout from './app/DashboardLayout';
// Business portal — PAUSED. Routes removed so it's unreachable; the web/src/business/* files
// and backend /business/* API stay in the tree for when we resume.

// PERF-16: route-level code splitting. One CRA bundle used to carry the marketing site, the
// operator console, AND every app page — a marketing visitor downloaded the admin console and
// the app downloaded the marketing site. Auth + the shell stay eager (they gate everything and
// are small); each destination loads its own chunk on first navigation.
const Admin = lazy(() => import('./Admin'));
const Home = lazy(() => import('./Marketing').then((m) => ({ default: m.Home })));
const About = lazy(() => import('./Marketing').then((m) => ({ default: m.About })));
const Contact = lazy(() => import('./Marketing').then((m) => ({ default: m.Contact })));
const Overview = lazy(() => import('./app/Overview'));
const Insights = lazy(() => import('./app/Insights'));
const Badges = lazy(() => import('./app/Badges'));
const Circles = lazy(() => import('./app/Circles'));
const CircleDetail = lazy(() => import('./app/CircleDetail'));
const Friends = lazy(() => import('./app/Friends'));
const FriendDetail = lazy(() => import('./app/FriendDetail'));
const Moments = lazy(() => import('./app/Moments'));
const MomentDetail = lazy(() => import('./app/MomentDetail'));
const PlannedMomentDetail = lazy(() => import('./app/PlannedMomentDetail'));
const Profile = lazy(() => import('./app/Profile'));
const Family = lazy(() => import('./app/Family'));
const FamilyChild = lazy(() => import('./app/FamilyChild'));
// Public account-deletion page (Google Play requires a web-accessible deletion path).
const DeleteAccount = lazy(() => import('./app/DeleteAccount'));
// Public, no-auth Community Pulse pages (city dashboards fed by the /community API).
const CommunityDirectory = lazy(() => import('./community/CommunityDirectory'));
const CommunityPage = lazy(() => import('./community/CommunityPage'));

// Minimal chunk-load fallback — dependency-free so the Suspense boundary itself doesn't pull
// app CSS into the entry bundle. Full-viewport and painted in the brand navy: this fallback
// only shows for FIRST entry into a public page's chunk (authed /app pages suspend at the
// nested boundary inside DashboardLayout instead), and without the background it flashed the
// white body behind the unmounted tree.
const chunkFallback = (
  <div style={{ minHeight: '100vh', background: '#07061A', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>
    Loading…
  </div>
);

// privacy.html / terms.html are real files in public/, served by the web server — not SPA routes.
// Some embedded webviews (and the desktop preview panel) intercept link clicks and pushState the
// URL instead of doing a document load; that used to fall into the "*" catch-all and bounce the
// visitor back to the homepage. These routes catch that case and force a genuine document request.
// The recent-retry guard stops a reload loop if a host ever serves the SPA shell at these paths.
function StaticDoc() {
  // Extensionless variants reach the SPA when a browser holds the old cached 301
  // (/privacy.html → /privacy from serve's former cleanUrls default) — resolve both
  // spellings to the real file.
  const { pathname } = window.location;
  const file = pathname.endsWith('.html') ? pathname : `${pathname}.html`;
  const [stuck, setStuck] = React.useState(false);
  React.useEffect(() => {
    const key = `static-doc-retry:${file}`;
    const last = Number(sessionStorage.getItem(key) || 0);
    if (Date.now() - last < 3000) { setStuck(true); return; }
    sessionStorage.setItem(key, String(Date.now()));
    window.location.replace(file);
  }, [file]);
  return (
    <div style={{ minHeight: '100vh', background: '#07061A', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>
      {stuck ? <a href={file} style={{ color: '#34d3f5' }}>Open {file.replace(/^\//, '')}</a> : 'Loading…'}
    </div>
  );
}

// Public marketing site at the root; the operator console lives at a non-obvious path.
// The authenticated end-user companion dashboard lives under /app — it can register,
// join/create circles, and start/participate in moments (near 1:1 with the mobile app;
// in-person verification and BLE stay app-only). Unknown routes fall back to the
// marketing home.
export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Suspense fallback={chunkFallback}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/internal/admin" element={<Admin />} />

            {/* Business portal routes removed — direction paused. */}

            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            {/* Public circle-invite landing — the URL shared from the app and Android App Links. */}
            <Route path="/join/:code" element={<JoinCircle />} />
            {/* Public personal-invite landing — sphere-sync.com/r/<code> (profile invite links). */}
            <Route path="/r/:code" element={<JoinReferral />} />
            {/* Public Community Pulse — no-auth city dashboards (directory + per-city page). */}
            <Route path="/community" element={<CommunityDirectory />} />
            <Route path="/community/:slug" element={<CommunityPage />} />
            {/* Public account-deletion path (Play User Data policy). Handles its own auth states. */}
            <Route path="/delete-account" element={<DeleteAccount />} />
            <Route
              path="/app"
              element={<RequireAuth><DashboardLayout /></RequireAuth>}
            >
              <Route index element={<Overview />} />
              <Route path="insights" element={<Insights />} />
              <Route path="badges" element={<Badges />} />
              <Route path="circles" element={<Circles />} />
              <Route path="circles/:crewId" element={<CircleDetail />} />
              <Route path="friends" element={<Friends />} />
              <Route path="friends/:friendId" element={<FriendDetail />} />
              <Route path="moments" element={<Moments />} />
              <Route path="moments/:momentId" element={<MomentDetail />} />
              <Route path="planned/:eventId" element={<PlannedMomentDetail />} />
              <Route path="profile" element={<Profile />} />
              <Route path="family" element={<Family />} />
              <Route path="family/:childId" element={<FamilyChild />} />
            </Route>

            {/* Static legal documents — never bounce these to the homepage (see StaticDoc). */}
            <Route path="/privacy.html" element={<StaticDoc />} />
            <Route path="/terms.html" element={<StaticDoc />} />
            <Route path="/privacy" element={<StaticDoc />} />
            <Route path="/terms" element={<StaticDoc />} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </AuthProvider>
  );
}
